import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import CONFIG from '../config';
import logo from '../asset/logo512.png';

const Login = ({ setIsAuthenticated, setUserInfo }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [animateLogo, setAnimateLogo] = useState(false);
  const [animateInput, setAnimateInput] = useState(false);
  const navigate = useNavigate();

  // Trigger animations on component mount
  useEffect(() => {
    const timer1 = setTimeout(() => setAnimateLogo(true), 300);
    const timer2 = setTimeout(() => setAnimateInput(true), 600);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      // Call backend login API using centralized config
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(CONFIG.getFullUrl('/auth/token'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      const token = data.access_token;

      // Store token
      localStorage.setItem('access_token', token);

      // Decode token to get user info
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Extract name from email (username part before @)
      const usernameFromEmail = email.split('@')[0];
      const capitalizedName = usernameFromEmail.charAt(0).toUpperCase() + usernameFromEmail.slice(1);
      
      const userInfo = {
        email: payload.sub,
        user_id: payload.user_id,
        role: payload.role,
        name: capitalizedName,
        token: token
      };

      // Store user info
      localStorage.setItem('currentUser', JSON.stringify(userInfo));
      
      // Update state
      setIsAuthenticated(true);
      setUserInfo(userInfo);
      
      console.log('✅ Login successful for:', userInfo.email, 'Role:', userInfo.role);

      // Redirect based on role
      if (userInfo.role === 'superadmin') {
        navigate('/superadmin', { replace: true });
      } else if (userInfo.role === 'admin') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/user-view', { replace: true });
      }

    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 to-gray-300 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/20 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          ></div>
        ))}
        
        {/* Animated gradient orbs */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl animate-pulse-slow-delayed"></div>
      </div>

      {/* Main Login Card */}
      <div className="relative z-10 w-full max-w-sm p-6 space-y-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl transition-all duration-500 hover:shadow-2xl animate-slide-up">
        <div className="text-center">
          {/* Logo with animation */}
          <div className="flex justify-center mb-3">
            <div className={`relative ${animateLogo ? 'animate-logo-pop' : 'opacity-0'}`}>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-md opacity-20 animate-pulse"></div>
              <img 
                src={logo} 
                alt="Company Logo" 
                className="relative w-16 h-16 rounded-full shadow-md border border-gray-200 dark:border-gray-700 object-cover animate-float-gentle"
              />
            </div>
          </div>

          {/* Title with typing animation */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 animate-typewriter">
            NIRA FINHUB
          </h2>
          
          {/* Subtitle with fade animation */}
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 animate-fade-in">
            Sign in to access your account
          </p>
        </div>

        {error && (
          <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md animate-shake">
            {error}
          </div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email Field */}
            <div className={`${animateInput ? 'animate-slide-in-left' : 'opacity-0'}`}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 animate-text-glow">
                Email Address
              </label>
              <div className="relative group">
                <input
                  type="email"
                  className="w-full px-3 py-2 pl-10 text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 group-hover:border-blue-400 animate-input-glow"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-bounce-slow">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Password Field */}
            <div className={`${animateInput ? 'animate-slide-in-right' : 'opacity-0'}`}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 animate-text-glow">
                Password
              </label>
              <div className="relative group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-3 py-2 pl-10 text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 group-hover:border-blue-400 animate-input-glow"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-bounce-slow-delayed">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300 animate-pulse"
                  disabled={isLoading}
                >
                  {showPassword ? <FaEyeSlash size={18} className="animate-flip" /> : <FaEye size={18} className="animate-flip" />}
                </button>
              </div>
            </div>
          </div>

          {/* Login Button */}
          <div className="animate-pop-in">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2.5 rounded-md text-white font-medium 
              bg-gradient-to-r from-blue-600 to-purple-600 
              hover:from-blue-700 hover:to-purple-700 
              transition-all duration-300 ${isLoading ? 'opacity-70 cursor-not-allowed animate-pulse' : 'shadow-md hover:shadow-lg'} relative overflow-hidden group`}
            >
              <span className="relative z-10 flex items-center justify-center animate-text-shimmer">
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </>
                ) : (
                  "Sign In"
                )}
              </span>
              
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Button glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
            </button>
          </div>
        </form>

        {/* Footer with fade animation */}
        <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700 animate-fade-in-delayed">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Secure Access • Powered by NIRA FINHUB
          </p>
        </div>
      </div>

      {/* Add custom animations */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes logoPop {
          0% {
            opacity: 0;
            transform: scale(0.5) rotate(-180deg);
          }
          70% {
            opacity: 1;
            transform: scale(1.1) rotate(10deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        @keyframes floatGentle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes floatParticle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          33% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.5;
          }
          66% {
            transform: translateY(10px) translateX(-10px);
            opacity: 0.3;
          }
        }
        
        @keyframes typewriter {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes bounceSlow {
          0%, 100% {
            transform: translateY(-50%) scale(1);
          }
          50% {
            transform: translateY(-50%) scale(1.1);
          }
        }
        
        @keyframes textGlow {
          0%, 100% {
            text-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
          }
          50% {
            text-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
          }
        }
        
        @keyframes inputGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          50% {
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
          }
        }
        
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(5px);
          }
        }
        
        @keyframes flip {
          0% {
            transform: rotateY(0deg);
          }
          100% {
            transform: rotateY(180deg);
          }
        }
        
        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          70% {
            opacity: 1;
            transform: scale(1.05);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes textShimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        
        @keyframes pulseSlow {
          0%, 100% {
            opacity: 0.1;
          }
          50% {
            opacity: 0.3;
          }
        }
        
        .animate-slide-up {
          animation: slideUp 0.6s ease-out;
        }
        
        .animate-logo-pop {
          animation: logoPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .animate-float-gentle {
          animation: floatGentle 3s ease-in-out infinite;
        }
        
        .animate-float-particle {
          animation: floatParticle 20s ease-in-out infinite;
        }
        
        .animate-typewriter {
          display: inline-block;
          overflow: hidden;
          white-space: nowrap;
          animation: typewriter 1.5s steps(15) 0.5s 1 normal both;
          border-right: 2px solid;
          border-color: #3b82f6;
          width: 0;
        }
        
        .animate-fade-in {
          animation: fadeIn 1s ease-out 1s both;
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.5s ease-out 0.8s both;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.5s ease-out 1s both;
        }
        
        .animate-bounce-slow {
          animation: bounceSlow 2s ease-in-out infinite;
        }
        
        .animate-bounce-slow-delayed {
          animation: bounceSlow 2s ease-in-out infinite 0.5s;
        }
        
        .animate-text-glow {
          animation: textGlow 2s ease-in-out infinite;
        }
        
        .animate-input-glow {
          animation: inputGlow 3s ease-in-out infinite;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        
        .animate-flip {
          animation: flip 0.3s ease-in-out;
        }
        
        .animate-pop-in {
          animation: popIn 0.5s ease-out 1.2s both;
        }
        
        .animate-text-shimmer {
          background: linear-gradient(90deg, #fff 0%, #a5b4fc 50%, #fff 100%);
          background-size: 200% auto;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: textShimmer 2s linear infinite;
        }
        
        .animate-pulse-slow {
          animation: pulseSlow 4s ease-in-out infinite;
        }
        
        .animate-pulse-slow-delayed {
          animation: pulseSlow 4s ease-in-out infinite 2s;
        }
        
        .animate-fade-in-delayed {
          animation: fadeIn 1s ease-out 1.5s both;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-slide-up,
          .animate-logo-pop,
          .animate-float-gentle,
          .animate-float-particle,
          .animate-typewriter,
          .animate-fade-in,
          .animate-slide-in-left,
          .animate-slide-in-right,
          .animate-bounce-slow,
          .animate-bounce-slow-delayed,
          .animate-text-glow,
          .animate-input-glow,
          .animate-shake,
          .animate-flip,
          .animate-pop-in,
          .animate-text-shimmer,
          .animate-pulse-slow,
          .animate-pulse-slow-delayed,
          .animate-fade-in-delayed {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;